import { useState, useRef, useEffect, useCallback } from 'react';
import {
  deleteIntakeDocument,
  getReferralDocs,
  stampIntakeDocument,
  updateIntakeDocument,
  uploadDocumentToIntake,
} from '../services/api';
import { useToast } from './useToastContext';
import type { IntakeRecord } from '../store/slices/intakeSlice';

interface DocumentInfo {
  id: string;
  fileName: string;
  mimeType?: string | null;
}

interface UseIntakeDocumentsOptions {
  intakeId: string | undefined;
  documents: Array<{ id: string }> | undefined;
  loadIntakeDetails: (intakeId: string) => Promise<unknown>;
}

/**
 * Merge freshly fetched presigned URLs into existing state WITHOUT overwriting
 * URLs we already hold. Only documents we don't yet have a URL for get one added.
 *
 * This is used by the background refresh paths (initial/count-change load and the
 * eligibility poll). Preserving the existing URL string for open documents keeps
 * the PDF viewer's iframe src byte-stable, so attaching new documents (e.g.
 * eligibility PDFs) does not reload the already-open PDF. Foreground refreshes
 * that intentionally replace a URL (stamp, doc switch, upload) do their own thing.
 */
function addNewDocumentUrls(
  prev: Record<string, string>,
  documents: Array<{ id?: string | null; url?: string | null }>
): Record<string, string> {
  // Copy lazily and return the original reference when nothing is added, so
  // React's Object.is bailout can skip a no-op re-render of the viewer.
  let next: Record<string, string> | null = null;
  for (const doc of documents) {
    if (doc.id && doc.url && !(doc.id in prev)) {
      if (!next) next = { ...prev };
      next[doc.id] = doc.url;
    }
  }
  return next ?? prev;
}

/**
 * Custom hook to manage intake document operations including:
 * - Loading document URLs
 * - Document renaming
 * - Document uploading
 * - Refreshing presigned URLs
 */
export function useIntakeDocuments({
  intakeId,
  documents,
  loadIntakeDetails,
}: UseIntakeDocumentsOptions) {
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const hasFetchedDocumentUrlsRef = useRef<string | null>(null);

  const { error: showToastError, success: showToastSuccess } = useToast();

  // Load document URLs when intake is loaded or document count changes
  const documentCount = documents?.length ?? 0;
  useEffect(() => {
    const loadDocumentUrls = async () => {
      // Skip if already fetched for this exact intake + document count
      const cacheKey = `${intakeId}:${documentCount}`;
      if (hasFetchedDocumentUrlsRef.current === cacheKey) return;
      if (!intakeId || !documents || documents.length === 0) return;

      hasFetchedDocumentUrlsRef.current = cacheKey;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
        });

        if (response.data?.documents) {
          const fetchedDocs = response.data.documents;
          // Only add URLs for documents we don't already have; preserving existing
          // URL strings keeps the open PDF from reloading when a new doc is attached.
          setDocumentUrls(prev => addNewDocumentUrls(prev, fetchedDocs));
        }
      } catch (err) {
        console.error('Error loading document URLs:', err);
      }
    };

    loadDocumentUrls();
  }, [documents, intakeId, documentCount]);

  // Set first document as active when documents change and no active document
  useEffect(() => {
    if (documents && documents.length > 0 && !activeDocumentId) {
      setActiveDocumentId(documents[0].id);
    }
  }, [documents, activeDocumentId]);

  // Handle document rename
  const handleDocumentRename = useCallback(
    async (documentId: string, fileName: string) => {
      try {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
        });
        showToastSuccess('Document renamed successfully.');
        // Refresh intake details to propagate changes
        if (intakeId) {
          await loadIntakeDetails(intakeId);
        }
      } catch (err) {
        console.error('Error renaming document:', err);
        showToastError('Failed to rename document.');
      }
    },
    [intakeId, loadIntakeDetails, showToastSuccess, showToastError]
  );

  // Handle document upload
  const handleDocumentAdd = useCallback(
    async (file: File, displayName: string): Promise<DocumentInfo | null> => {
      if (!intakeId) return null;

      try {
        const response = await uploadDocumentToIntake({
          path: { intake_id: intakeId },
          query: { display_name: displayName },
          body: { file },
        });

        if (response.data) {
          // Refresh intake to update selectedIntake.documents
          await loadIntakeDetails(intakeId);

          // Generate presigned URL for the new document
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeId },
          });

          if (docsResponse.data?.documents) {
            const urlMap: Record<string, string> = {};
            docsResponse.data.documents.forEach(doc => {
              if (doc.id && doc.url) urlMap[doc.id] = doc.url;
            });
            setDocumentUrls(urlMap);
          }

          return {
            id: response.data.id,
            fileName: response.data.file_name,
            mimeType: response.data.mime_type ?? null,
          };
        }
        return null;
      } catch (err) {
        console.error('Error uploading document:', err);
        showToastError(
          'Upload failed',
          'Failed to upload document. Please try again.'
        );
        return null;
      }
    },
    [intakeId, loadIntakeDetails, showToastError]
  );

  const handleDocumentDelete = useCallback(
    async (documentId: string): Promise<void> => {
      try {
        await deleteIntakeDocument({
          path: { document_id: documentId },
          throwOnError: true,
        });

        setDocumentUrls(prev => {
          const next = { ...prev };
          delete next[documentId];
          return next;
        });
        hasFetchedDocumentUrlsRef.current = null;
        showToastSuccess('Document deleted successfully.');

        if (intakeId) {
          try {
            await loadIntakeDetails(intakeId);
          } catch (refreshError) {
            console.error(
              'Error refreshing intake after document delete:',
              refreshError
            );
          }
        }
      } catch (err) {
        console.error('Error deleting document:', err);
        showToastError('Failed to delete document. Please try again.');
        throw err;
      }
    },
    [intakeId, loadIntakeDetails, showToastSuccess, showToastError]
  );

  const handleDocumentStamp = useCallback(
    async (documentId: string): Promise<void> => {
      try {
        await stampIntakeDocument({
          path: { document_id: documentId },
        });

        showToastSuccess('Stamp page appended successfully.');

        if (intakeId) {
          await loadIntakeDetails(intakeId);

          // Refresh presigned URLs so the viewer shows the updated PDF
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeId },
          });
          if (docsResponse.data?.documents) {
            const urlMap: Record<string, string> = {};
            docsResponse.data.documents.forEach(doc => {
              if (doc.id && doc.url) urlMap[doc.id] = doc.url;
            });
            setDocumentUrls(urlMap);
          }
        }
      } catch (err) {
        console.error('Error stamping document:', err);
        showToastError('Failed to append stamp page. Please try again.');
      }
    },
    [intakeId, loadIntakeDetails, showToastSuccess, showToastError]
  );

  /**
   * Handle refreshing document URL to get a fresh presigned URL.
   *
   * NOTE: This currently fetches ALL document URLs from the API and extracts the one we need.
   * This is a quick 80/20 fix. A more optimized approach would be to add a dedicated API
   * endpoint like GET /intake/{intake_id}/documents/{document_id}/url that returns only
   * the single document's presigned URL, reducing payload size and server load.
   */
  const handleRefreshDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      if (!intakeId) return null;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
        });

        if (response.data?.documents) {
          const doc = response.data.documents.find(d => d.id === documentId);
          if (doc) {
            // Update the documentUrls state with fresh URLs
            const urlMap: Record<string, string> = {};
            response.data.documents.forEach(d => {
              if (d.id && d.url) urlMap[d.id] = d.url;
            });
            setDocumentUrls(urlMap);
            return doc.url;
          }
        }
        return null;
      } catch (err) {
        console.error('Error refreshing document URL:', err);
        return null;
      }
    },
    [intakeId]
  );

  /** Reload intake documents and presigned URLs (bypasses URL cache). */
  const refreshDocuments = useCallback(async () => {
    if (!intakeId) return [];

    hasFetchedDocumentUrlsRef.current = null;
    const intake = (await loadIntakeDetails(intakeId)) as
      | IntakeRecord
      | undefined;
    const docs = intake?.documents ?? [];

    try {
      const response = await getReferralDocs({
        path: { intake_id: intakeId },
      });

      if (response.data?.documents) {
        const fetchedDocs = response.data.documents;
        // Only add URLs for documents we don't already have; preserving existing
        // URL strings keeps the open PDF from reloading during the eligibility poll.
        setDocumentUrls(prev => addNewDocumentUrls(prev, fetchedDocs));
        hasFetchedDocumentUrlsRef.current = `${intakeId}:${docs.length}`;
      }
    } catch (err) {
      console.error('Error refreshing document URLs:', err);
    }

    return docs;
  }, [intakeId, loadIntakeDetails]);

  return {
    documentUrls,
    activeDocumentId,
    setActiveDocumentId,
    handleDocumentRename,
    handleDocumentAdd,
    handleDocumentDelete,
    handleDocumentStamp,
    handleRefreshDocumentUrl,
    refreshDocuments,
  };
}
